import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sanitizeHtml } from '../utils/sanitizeHtml';
import { normalizeFrame, MIN_TEXT_SCALE, MAX_TEXT_SCALE } from '../utils/slideFrames.js';
import {
    emptyGuides,
    loadGuides,
    saveGuides,
    loadRulerPref,
    sanitizeGuides,
    snapFrameToGuides,
    tidyGuidePosition
} from '../utils/slideGuides.js';
import { detectQuotes, quoteToBlockData, removeQuotesAt } from '../utils/quoteDetection.js';
import { writeBlockClipboard, readBlockClipboard, prepareBlocksForPaste } from '../utils/blockClipboard.js';
import { hasPlaylistCitationDetails, playlistPageUrl } from '../utils/youtubePlaylist.js';

// Major/minor tick marks drawn along each ruler, in percent of the slide.
const MAJOR_TICKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const MINOR_TICKS = Array.from({ length: 51 }, (_, i) => i * 2).filter((p) => p % 10 !== 0);

// Block types whose content scales uniformly with the frame (like objects
// in PowerPoint) instead of flowing/cropping like text does
const SCALED_MEDIA_TYPES = new Set(['image', 'video', 'youtube-playlist', 'gallery', 'embedded-html', 'project-timeline']);

// Block types that keep their body as rich-text HTML on `block.content`, so
// they can be typed straight into on the canvas (double-click) instead of
// having to round-trip through the full editor modal.
const INLINE_TEXT_TYPES = new Set(['text', 'heading', 'list', 'table', 'headline']);

// Short human label shown on a selected object's tab handle.
const BLOCK_TAB_LABELS = {
    text: 'Text',
    heading: 'Heading',
    headline: 'Headline',
    list: 'List',
    table: 'Table',
    image: 'Image',
    video: 'Video',
    'youtube-playlist': 'Playlist',
    audio: 'Audio',
    gallery: 'Gallery',
    link: 'Link',
    divider: 'Divider',
    html: 'Code',
    'embedded-html': 'Embed',
    'custom-html': 'HTML/CSS',
    'info-box': 'Info',
    'exercise-box': 'Exercise',
    'warning-box': 'Warning',
    'apa-quote': 'Quote',
    'apa-citation': 'Citation',
    'apa-reference-list': 'References',
    'project-timeline': 'Timeline',
    accordion: 'Accordion',
    cards: 'Cards',
    'multi-column-cards': 'Cards',
    'stacked-cards': 'Cards',
    worksheet: 'Worksheet',
    image_drawover: 'Drawing'
};

const tabLabel = (block) => BLOCK_TAB_LABELS[block?.type]
    || (block?.type ? String(block.type).replace(/[-_]/g, ' ') : 'Block');

// The rich-text wrapper class each inline-editable type renders with, so the
// editable surface inherits exactly the typography of the rendered block.
const inlineEditorClass = (type) => (type === 'headline' ? 'headline-preview' : 'rich-editor-content');

/**
 * In-place rich-text editing for a canvas text object. The block's HTML is
 * loaded into a contentEditable surface sized by the frame, so what you type is
 * laid out (and text-scaled) exactly as it will render — no modal round trip.
 * A compact ribbon floats beside the box with the common formatting commands.
 *
 * Editing ends on Escape, on a click outside the box, or via the ribbon's ✓,
 * and always commits what is in the box (nothing typed is thrown away).
 */
const InlineTextEditor = ({ block, ribbonBelow, ribbonRight, caretPoint, onCommit, onOpenModal }) => {
    const editorRef = useRef(null);
    // Latest HTML, kept in a ref so the unmount/outside-click paths can commit
    // without re-reading a detached node.
    const htmlRef = useRef(block.content || '');
    const commitRef = useRef(onCommit);
    useEffect(() => { commitRef.current = onCommit; }, [onCommit]);

    // Seed the editable surface once — React must not re-render its children or
    // the caret would jump on every keystroke.
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        // Seeding a contentEditable is a live-DOM write, so the stored block
        // content goes through the sanitizer here too.
        el.innerHTML = sanitizeHtml(block.content);
        el.focus();
        // Drop the caret where the user double-clicked when the browser can tell
        // us; otherwise park it at the end of the content.
        const selection = window.getSelection?.();
        let range = null;
        if (caretPoint && typeof document.caretRangeFromPoint === 'function') {
            range = document.caretRangeFromPoint(caretPoint.x, caretPoint.y);
        }
        if (!range && typeof document.createRange === 'function') {
            range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
        }
        if (range && selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clicking anywhere outside the box (or its ribbon) finishes the edit.
    useEffect(() => {
        const onDocPointerDown = (event) => {
            const target = event.target;
            if (target?.closest?.('.slide-designer__inline-editor, .slide-designer__ribbon')) return;
            // The click itself decides where focus lands, so don't pull it back
            // to the canvas.
            commitRef.current?.(htmlRef.current, { refocusStage: false });
        };
        document.addEventListener('pointerdown', onDocPointerDown, true);
        return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
    }, []);

    const syncHtml = useCallback(() => {
        if (editorRef.current) htmlRef.current = editorRef.current.innerHTML;
    }, []);

    // document.execCommand is deprecated but is still the only cross-browser way
    // to format a plain contentEditable surface, and it keeps this lightweight
    // editor free of a second editor engine on the canvas.
    const exec = useCallback((command, value = null) => {
        editorRef.current?.focus();
        try {
            document.execCommand(command, false, value);
        } catch {
            /* command unsupported in this browser — ignore */
        }
        syncHtml();
    }, [syncHtml]);

    const applyBlockFormat = useCallback((tag) => exec('formatBlock', tag), [exec]);

    const addLink = useCallback(() => {
        const url = window.prompt('Link URL');
        if (url === null) return;
        if (url.trim() === '') exec('unlink');
        else exec('createLink', url.trim());
    }, [exec]);

    // A headline block is a single heading node in the modal editor, so Enter
    // must not split it into extra blocks that the modal would drop.
    const isHeadline = block.type === 'headline';

    const onKeyDown = useCallback((event) => {
        // Keep canvas shortcuts (delete/nudge/group) from firing while typing.
        event.stopPropagation();
        if (isHeadline && event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
            event.preventDefault();
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            syncHtml();
            commitRef.current?.(htmlRef.current);
            return;
        }
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            syncHtml();
            commitRef.current?.(htmlRef.current);
        }
    }, [syncHtml, isHeadline]);

    // Paste as plain text so slide styling survives a copy from the web.
    const onPaste = useCallback((event) => {
        const text = event.clipboardData?.getData('text/plain');
        if (text === undefined || typeof document.execCommand !== 'function') return;
        event.preventDefault();
        document.execCommand('insertText', false, text);
        syncHtml();
    }, [syncHtml]);

    const btn = (key, label, title, onClick, extraClass = '') => (
        <button
            key={key}
            type="button"
            className={`slide-designer__ribbonbtn ${extraClass}`}
            title={title}
            // Never let the button steal the selection from the editor.
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
        >
            {label}
        </button>
    );

    return (
        <>
            <div
                className={`slide-designer__ribbon ${ribbonBelow ? 'slide-designer__ribbon--below' : ''} ${ribbonRight ? 'slide-designer__ribbon--right' : ''}`}
                onPointerDown={(e) => e.stopPropagation()}
                // Clicking the ribbon (buttons or the space between them) must
                // never pull the caret out of the text being edited.
                onMouseDown={(e) => e.preventDefault()}
                role="toolbar"
                aria-label="Text formatting"
            >
                <span className="slide-designer__ribbongroup">
                    {btn('bold', <b>B</b>, 'Bold (Ctrl/⌘+B)', () => exec('bold'))}
                    {btn('italic', <i>I</i>, 'Italic (Ctrl/⌘+I)', () => exec('italic'))}
                    {btn('underline', <u>U</u>, 'Underline (Ctrl/⌘+U)', () => exec('underline'))}
                    {btn('strike', <s>S</s>, 'Strikethrough', () => exec('strikeThrough'))}
                </span>
                <span className="slide-designer__ribbongroup">
                    {!isHeadline && btn('p', 'P', 'Paragraph', () => applyBlockFormat('p'))}
                    {btn('h1', 'H1', 'Heading 1', () => applyBlockFormat('h1'))}
                    {btn('h2', 'H2', 'Heading 2', () => applyBlockFormat('h2'))}
                    {!isHeadline && btn('h3', 'H3', 'Heading 3', () => applyBlockFormat('h3'))}
                </span>
                {!isHeadline && (
                    <span className="slide-designer__ribbongroup">
                        {btn('ul', '•', 'Bulleted list', () => exec('insertUnorderedList'))}
                        {btn('ol', '1.', 'Numbered list', () => exec('insertOrderedList'))}
                    </span>
                )}
                <span className="slide-designer__ribbongroup">
                    {btn('left', '⇤', 'Align left', () => exec('justifyLeft'))}
                    {btn('center', '↔', 'Align centre', () => exec('justifyCenter'))}
                    {btn('right', '⇥', 'Align right', () => exec('justifyRight'))}
                </span>
                <span className="slide-designer__ribbongroup">
                    {btn('link', '🔗', 'Add or remove a link', addLink)}
                    {btn('clear', '⌫', 'Clear formatting', () => exec('removeFormat'))}
                </span>
                <span className="slide-designer__ribbongroup">
                    {btn('modal', '⤢', 'Open the full editor modal', () => { syncHtml(); onOpenModal?.(htmlRef.current); })}
                    {btn(
                        'done',
                        '✓',
                        'Finish editing (Esc)',
                        () => { syncHtml(); commitRef.current?.(htmlRef.current); },
                        'slide-designer__ribbonbtn--done'
                    )}
                </span>
            </div>
            <div
                ref={editorRef}
                className={`slide-designer__inline-editor ${inlineEditorClass(block.type)}`}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label="Edit block text on the slide"
                spellCheck
                onInput={syncHtml}
                onBlur={syncHtml}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onPointerDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
            />
        </>
    );
};

/**
 * Renders content at the frame's width, measures its natural height, and
 * uniformly scales it down so it always fits the frame — resize the frame
 * and the media scales with it. Mirrors the PPTX export's contain-fit.
 */
const ScaleToFit = ({ children, onMeasure }) => {
    const outerRef = useRef(null);
    const innerRef = useRef(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const outer = outerRef.current;
        const inner = innerRef.current;
        if (!outer || !inner || typeof ResizeObserver === 'undefined') return undefined;

        const update = () => {
            const frameHeight = outer.clientHeight;
            const naturalHeight = inner.scrollHeight;
            if (!frameHeight || !naturalHeight) return;
            onMeasure?.(naturalHeight);
            const next = naturalHeight > frameHeight
                ? Math.max(0.05, frameHeight / naturalHeight)
                : 1;
            setScale((prev) => (Math.abs(prev - next) > 0.01 ? next : prev));
        };

        const observer = new ResizeObserver(update);
        observer.observe(outer);
        observer.observe(inner);
        update();
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div ref={outerRef} className="slide-designer__fit">
            <div
                ref={innerRef}
                className="slide-designer__fit-inner"
                style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}
            >
                {children}
            </div>
        </div>
    );
};

// Frame geometry is stored on each block as percentages of the full 16:9
// slide: { x, y, w, h } with 0-100 on both axes. The PPTX exporter maps
// these percentages directly onto slide inches, so what you lay out here
// is exactly what exports.

const SNAP_TOLERANCE = 1.2;

const applySnaps = (frame) => {
    const snapped = { ...frame };
    const centerX = frame.x + frame.w / 2;
    const centerY = frame.y + frame.h / 2;
    if (Math.abs(centerX - 50) < SNAP_TOLERANCE) snapped.x = 50 - frame.w / 2;
    if (Math.abs(centerY - 50) < SNAP_TOLERANCE) snapped.y = 50 - frame.h / 2;
    if (Math.abs(frame.x - 4) < SNAP_TOLERANCE) snapped.x = 4;
    if (Math.abs(frame.x + frame.w - 96) < SNAP_TOLERANCE) snapped.x = 96 - frame.w;
    if (Math.abs(frame.y - 4) < SNAP_TOLERANCE) snapped.y = 4;
    if (Math.abs(frame.y + frame.h - 96) < SNAP_TOLERANCE) snapped.y = 96 - frame.h;
    return snapped;
};

// How close to the top of the stage an object can sit before its toolbar has to
// move inside it, as a percentage of slide height. The toolbar sits 34px above
// the object and a stage is roughly 520–560px tall, so ~7% is where it starts
// being clipped; 8 leaves a little room at narrower widths.
const TOOLBAR_FLIP_Y = 8;

const RESIZE_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

// Block types that carry a citation that can be split off into its own movable
// object (see detachCitation).
const CITATION_SOURCE_TYPES = new Set(['image', 'gallery', 'video', 'youtube-playlist']);

// The block field that suppresses a source's own inline note once its citation
// has been detached, so the note is never shown twice.
const INCLUDE_CITATION_FIELD = {
    image: 'imageIncludeCitation',
    video: 'videoIncludeCitation',
    // A playlist card keeps its source under the video block's field names.
    'youtube-playlist': 'videoIncludeCitation'
};

// A short-lived unique id for objects created inside the designer (detached
// citation blocks, group ids). Kept local so the component has no dependency on
// the app-wide id generator.
let uidCounter = 0;
const uid = (prefix = 'obj') => `${prefix}-${Date.now().toString(36)}-${(uidCounter++).toString(36)}`;

// Does the block already carry enough info to render an APA note?
export const blockHasCitationContent = (block) => {
    if (!block || !CITATION_SOURCE_TYPES.has(block.type)) return false;
    if (block.type === 'gallery') return false; // galleries hold many items; detach per-image is out of scope
    // A playlist card offers the tear-off exactly when it is drawing a note of
    // its own — the same rule, so the action can never detach the bare "(n.d.)"
    // that a card carrying nothing but its default "YouTube" source would make.
    if (block.type === 'youtube-playlist') {
        if (block.videoIncludeCitation === false) return false;
        return hasPlaylistCitationDetails(block);
    }
    if (block.type === 'video') {
        if (block.videoIncludeCitation === false) return false;
        // A video's note shows the in-text citation *and* the watch URL, so a
        // video with nothing but a link still has a note worth tearing off.
        // The URL has to be a real address though: a raw-embed video keeps its
        // markup in `src`, and that is not something to hand to a reader.
        const watchUrl = (block.videoUrl || block.src || '').trim();
        return Boolean(
            block.videoAuthor || block.videoTitle || block.videoSource || block.videoDate
            || /^https?:\/\//i.test(watchUrl)
        );
    }
    if (block.imageIncludeCitation === false) return false;
    return Boolean(block.imageAuthor || block.imageTitle || block.imageSource || block.imageDate);
};

// Turn an image block's citation metadata into a standalone citation object of
// the shape apa-citation blocks (and the reference-list collector) understand.
export const imageBlockToCitation = (block) => ({
    id: uid('cite'),
    type: 'image',
    sourceType: block.imageSourceType || 'image',
    author: block.imageAuthor || '',
    title: block.imageTitle || '',
    source: block.imageSource || '',
    date: block.imageDate || '',
    // Embedded images live behind a provider URL; carrying it over keeps the
    // detached citation clickable in the reference list.
    url: block.imageUrl || block.imageEmbedUrl || '',
    page: block.imageIncludePage === false ? '' : (block.imagePage || ''),
    license: block.imageIncludeLicense === false ? '' : (block.imageLicense || ''),
    isAdapted: Boolean(block.imageIsAdapted),
    // Reference-entry fields: which reference template the source falls under,
    // its medium descriptor and, where the two differ, the sentence-case title.
    referenceCategory: block.imageReferenceCategory || '',
    medium: block.imageMedium || '',
    referenceTitle: block.imageReferenceTitle || '',
    articleTitle: block.imageArticleTitle || '',
    caption: block.caption || '',
    alt: block.alt || ''
});

// The same, for a video block. `sourceType: 'video'` is what makes the detached
// note format as an in-text citation rather than a figure note — matching the
// note the video itself was drawing — and what the reference-list collector
// reads to build the full reference entry.
export const videoBlockToCitation = (block) => ({
    id: uid('cite'),
    type: 'video',
    sourceType: 'video',
    author: block.videoAuthor || '',
    title: block.videoTitle || '',
    source: block.videoSource || '',
    date: block.videoDate || '',
    // buildVideoSourceMeta falls back to the embed src, so a video whose only
    // identity is its URL still tears off something clickable.
    url: block.videoUrl || block.src || ''
});

// A playlist card's citation. Same shape as a video's — `sourceType: 'video'`
// is what makes the detached note format as an in-text citation and what the
// reference-list collector reads — with the card's own display fields as a
// fallback and the playlist page as the address.
export const playlistBlockToCitation = (block) => ({
    id: uid('cite'),
    type: 'video',
    sourceType: 'video',
    author: block.videoAuthor || block.channel || '',
    title: block.videoTitle || block.title || '',
    source: block.videoSource || 'YouTube',
    date: block.videoDate || '',
    // The address travels with the citation so the reference list can build the
    // full entry — but the note itself does not print it, matching the note the
    // card was drawing before it was torn off.
    url: block.videoUrl || playlistPageUrl(block.playlistId) || '',
    showUrl: false
});

export const blockToDetachedCitation = (block) => {
    if (block?.type === 'youtube-playlist') return playlistBlockToCitation(block);
    return block?.type === 'video' ? videoBlockToCitation(block) : imageBlockToCitation(block);
};

// Height for a popped-out quote's frame, in slide percent.
//
// The APA Quote card is chunky — padding, a badge row, the quote at text-lg and
// a citation line — so an undersized frame crops it. Measured on the canvas: a
// three-line quote in a 46%-wide frame needs ~50% of the slide height, which is
// the calibration behind the numbers below.
const QUOTE_CARD_CHROME = 32;   // badge + padding + citation line
const QUOTE_CARD_LINE = 6;      // one wrapped line of quote text

// detectQuotes parses HTML, so memoize the count against the exact content the
// selected block is showing — re-selecting or nudging a block should not
// re-parse it.
let quoteCountCache = { content: null, count: 0 };
const countQuotes = (content) => {
    if (quoteCountCache.content === content) return quoteCountCache.count;
    const count = detectQuotes(content).length;
    quoteCountCache = { content, count };
    return count;
};

const estimateQuoteFrameHeight = (quote, widthPercent) => {
    const charsPerLine = Math.max(16, Math.round(widthPercent * 0.8));
    const lines = Math.max(1, Math.ceil((quote.quoteText || '').length / charsPerLine));
    return Math.min(65, QUOTE_CARD_CHROME + lines * QUOTE_CARD_LINE);
};

// Rectangle-overlap test in slide-percent space, used by the marquee selection.
const framesIntersect = (a, b) => !(
    a.x > b.x + b.w || a.x + a.w < b.x || a.y > b.y + b.h || a.y + a.h < b.y
);

// True when focus is inside editable content (a form field or rich-text area),
// so keyboard shortcuts like delete don't fire while the user is typing.
const isEditableTarget = (target) => {
    if (!target || typeof target.closest !== 'function') return false;
    if (target.isContentEditable) return true;
    return Boolean(target.closest('input, textarea, select, [contenteditable="true"], .ProseMirror'));
};

/**
 * Fixed 16:9 canvas where blocks are positioned and scaled freely,
 * PowerPoint-style. Move by dragging, resize with the corner/edge
 * handles, nudge with arrow keys, reorder depth with the toolbar.
 */
const SlideDesigner = ({
    blocks = [],
    isEditMode = false,
    onUpdateBlocks,
    onEditBlock,
    onDeleteBlock,
    onCanvasImageDrop,
    // Ruler/guide visibility is controlled by the parent (its toggle lives in
    // the mode banner). Falls back to local state if the parent doesn't wire it.
    showRulers: showRulersProp,
    onToggleRulers,
    renderBlock
}) => {
    const stageRef = useRef(null);
    const dragRef = useRef(null);
    // Multiple objects can be selected at once. `selectedIds` is the source of
    // truth; `selectedId` is derived for the single-selection gizmo/toolbar.
    const [selectedIds, setSelectedIds] = useState([]);
    const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
    const [liveFrames, setLiveFrames] = useState({});
    // Block being typed into directly on the canvas (double-click), plus the
    // screen point that started it so the caret can land where the user clicked.
    const [inlineEdit, setInlineEdit] = useState(null); // { blockId, caretPoint }
    // Rubber-band selection rectangle (percent coords) while dragging on the
    // empty canvas, and whether an image file is being dragged over the stage.
    const [marquee, setMarquee] = useState(null);
    const marqueeRef = useRef(null);
    const [isFileOver, setIsFileOver] = useState(false);
    // Keep the latest blocks/selection reachable from the pointer handlers,
    // whose useCallbacks intentionally carry no deps.
    const blocksRef = useRef(blocks);
    useEffect(() => { blocksRef.current = blocks; }, [blocks]);
    const selectedIdsRef = useRef(selectedIds);
    useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

    // Ruler + guide-line overlay. Guides live only here (never in block/slide
    // data) so they are purely an editing aid and never export or render.
    // The rulers on/off flag is controlled from the parent when provided, so
    // its toggle button can live in the mode banner instead of over the title.
    const [localShowRulers] = useState(loadRulerPref);
    const showRulers = showRulersProp !== undefined ? showRulersProp : localShowRulers;
    const [guides, setGuides] = useState(loadGuides);
    // Live preview while a guide is being dragged from a ruler or repositioned.
    const [guideDrag, setGuideDrag] = useState(null); // { axis, pos, index, origin }
    const guideDragRef = useRef(null);
    // Keep the latest guides reachable from the block-drag pointer handler,
    // whose useCallback intentionally has no deps.
    const guidesRef = useRef(guides);
    useEffect(() => { guidesRef.current = guides; }, [guides]);
    useEffect(() => { saveGuides(guides); }, [guides]);
    // Latest measured natural content height (px) per media block, and the
    // set of blocks already auto-fitted so it only happens on first import
    const measurementsRef = useRef({});
    const autoFittedRef = useRef(new Set());

    // Leaving edit mode (or losing the block to a delete/slide change) always
    // drops the in-place editor so it can't linger over presented content.
    useEffect(() => {
        if (!inlineEdit) return;
        if (!isEditMode || !blocks.some((b) => b.id === inlineEdit.blockId)) setInlineEdit(null);
    }, [isEditMode, blocks, inlineEdit]);

    const frameOf = useCallback((block, index) => {
        return liveFrames[block.id] || normalizeFrame(block.frame, index);
    }, [liveFrames]);

    const commitFrame = useCallback((blockId, frame) => {
        onUpdateBlocks(blocks.map((b) => (b.id === blockId ? { ...b, frame } : b)));
        setLiveFrames((prev) => {
            const next = { ...prev };
            delete next[blockId];
            return next;
        });
    }, [blocks, onUpdateBlocks]);

    // Commit several frames at once (multi-object move), in a single update so
    // the whole selection stays together in one undo step.
    const commitFrames = useCallback((frameMap) => {
        const ids = Object.keys(frameMap);
        if (!ids.length) return;
        onUpdateBlocks(blocksRef.current.map((b) => (frameMap[b.id] ? { ...b, frame: frameMap[b.id] } : b)));
        setLiveFrames((prev) => {
            const next = { ...prev };
            ids.forEach((id) => delete next[id]);
            return next;
        });
    }, [onUpdateBlocks]);

    // Every block id that shares a group with the given block (or just itself
    // when it belongs to no group). Selecting one group member selects them all.
    const groupMemberIds = useCallback((block) => {
        if (!block?.groupId) return [block.id];
        return blocksRef.current.filter((b) => b.groupId === block.groupId).map((b) => b.id);
    }, []);

    // Select a block (expanding to its whole group). Additive (shift/⌘) toggles
    // the group in/out; a plain click on an already-selected block keeps the
    // current multi-selection so it can be dragged as a unit.
    const selectBlock = useCallback((block, additive) => {
        const members = groupMemberIds(block);
        setSelectedIds((prev) => {
            if (additive) {
                const allIn = members.every((id) => prev.includes(id));
                return allIn
                    ? prev.filter((id) => !members.includes(id))
                    : Array.from(new Set([...prev, ...members]));
            }
            if (prev.includes(block.id)) return prev;
            return members;
        });
    }, [groupMemberIds]);

    // --- In-place text editing -------------------------------------------
    // Start typing straight into a text object on the canvas. Non-text blocks
    // (images, cards, code…) still route to the full editor modal.
    const beginInlineEdit = useCallback((block, caretPoint = null) => {
        if (!isEditMode) return false;
        if (!INLINE_TEXT_TYPES.has(block.type)) return false;
        setSelectedIds([block.id]);
        selectedIdsRef.current = [block.id];
        setInlineEdit({ blockId: block.id, caretPoint });
        return true;
    }, [isEditMode]);

    // Write the edited HTML back onto the block and leave inline edit mode.
    // Only writes when something actually changed, so a no-op edit doesn't
    // push a pointless undo step.
    const commitInlineEdit = useCallback((blockId, html, { refocusStage = true } = {}) => {
        setInlineEdit(null);
        // Escape/✓ leave the object selected, so hand focus to the stage and the
        // canvas shortcuts (Delete, arrows) keep working straight away.
        if (refocusStage) stageRef.current?.focus({ preventScroll: true });
        const target = blocksRef.current.find((b) => b.id === blockId);
        if (!target) return;
        const clean = sanitizeHtml(html ?? '');
        if ((target.content || '') === clean) return;
        onUpdateBlocks(blocksRef.current.map((b) => (b.id === blockId ? { ...b, content: clean } : b)));
    }, [onUpdateBlocks]);

    // "Open the full editor" from the inline ribbon: keep what was typed, then
    // hand the block to the parent's modal.
    const escalateToModal = useCallback((blockId, html) => {
        // The modal takes focus itself — don't fight it for the caret.
        commitInlineEdit(blockId, html, { refocusStage: false });
        onEditBlock?.(blockId);
    }, [commitInlineEdit, onEditBlock]);

    const onPointerMove = useCallback((event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const { rect, start, mode, handle } = drag;
        const dx = ((event.clientX - start.clientX) / rect.width) * 100;
        const dy = ((event.clientY - start.clientY) / rect.height) * 100;
        let frame = { ...start.frame };

        if (mode === 'move') {
            frame.x = start.frame.x + dx;
            frame.y = start.frame.y + dy;
        } else {
            if (handle.includes('e')) frame.w = start.frame.w + dx;
            if (handle.includes('s')) frame.h = start.frame.h + dy;
            if (handle.includes('w')) { frame.x = start.frame.x + dx; frame.w = start.frame.w - dx; }
            if (handle.includes('n')) { frame.y = start.frame.y + dy; frame.h = start.frame.h - dy; }
            // Corner handles on text/title blocks scale the text with the box,
            // PowerPoint-style. Media already scales via ScaleToFit, and edge
            // handles keep resizing/reflowing the box without touching the text.
            const isCorner = handle.length === 2;
            if (isCorner && !SCALED_MEDIA_TYPES.has(drag.blockType) && start.frame.h > 0) {
                const startScale = start.frame.s || 1;
                const ratio = frame.h / start.frame.h;
                frame.s = Math.min(MAX_TEXT_SCALE, Math.max(MIN_TEXT_SCALE, startScale * ratio));
            }
        }
        frame = applySnaps(normalizeFrame(frame));
        // Snap the block onto any user-placed ruler guides (Illustrator-style).
        if (mode === 'move') frame = normalizeFrame(snapFrameToGuides(frame, guidesRef.current));
        drag.latest = frame;

        // Move: carry every other selected object by the same net delta so the
        // whole selection (or group) travels together.
        const nextLive = { [drag.blockId]: frame };
        if (mode === 'move' && drag.others?.length) {
            const gdx = frame.x - start.frame.x;
            const gdy = frame.y - start.frame.y;
            drag.others.forEach((other) => {
                const moved = normalizeFrame({ ...other.frame, x: other.frame.x + gdx, y: other.frame.y + gdy });
                nextLive[other.blockId] = moved;
            });
        }
        drag.latestMap = nextLive;
        setLiveFrames((prev) => ({ ...prev, ...nextLive }));
    }, []);

    const onPointerUp = useCallback(() => {
        const drag = dragRef.current;
        dragRef.current = null;
        window.removeEventListener('pointermove', onPointerMove);
        if (drag?.latestMap) commitFrames(drag.latestMap);
        else if (drag?.latest) commitFrame(drag.blockId, drag.latest);
        else if (drag) setLiveFrames((prev) => {
            const next = { ...prev };
            delete next[drag.blockId];
            return next;
        });
    }, [commitFrame, commitFrames, onPointerMove]);

    const startDrag = useCallback((event, block, index, mode, handle = null) => {
        if (!isEditMode || !stageRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        // preventDefault above stops the browser handing focus to the stage, so
        // do it here: the canvas keyboard shortcuts (Delete, arrows, ⌘G) only
        // fire while the stage — or a selected object's tab — holds focus.
        if (!event.currentTarget?.dataset?.keepFocus) {
            stageRef.current.focus({ preventScroll: true });
        }

        // Resolve the selection this drag operates on *synchronously* (state
        // updates are async, so we can't read it back from selectedIdsRef in the
        // same tick). A plain click on an already-selected object keeps the
        // whole multi-selection so it can be dragged as a unit; otherwise the
        // grabbed object (expanded to its group) becomes the new selection.
        const additive = event.shiftKey || event.metaKey || event.ctrlKey;
        const members = groupMemberIds(block);
        const prev = selectedIdsRef.current;
        let nextSelection;
        if (additive) {
            const allIn = members.every((id) => prev.includes(id));
            nextSelection = allIn
                ? prev.filter((id) => !members.includes(id))
                : Array.from(new Set([...prev, ...members]));
        } else if (prev.includes(block.id)) {
            nextSelection = prev;
        } else {
            nextSelection = members;
        }
        setSelectedIds(nextSelection);
        selectedIdsRef.current = nextSelection;

        // Which other objects travel with this one on a move: everything in the
        // resolved selection except the grabbed block itself. Resize only ever
        // affects the single grabbed block.
        let others = [];
        if (mode === 'move') {
            const moving = new Set(nextSelection);
            moving.delete(block.id);
            others = blocksRef.current
                .map((b, i) => ({ block: b, index: i }))
                .filter(({ block: b }) => moving.has(b.id))
                .map(({ block: b, index: i }) => ({ blockId: b.id, frame: frameOf(b, i) }));
        }

        dragRef.current = {
            blockId: block.id,
            blockType: block.type,
            mode,
            handle,
            others,
            rect: stageRef.current.getBoundingClientRect(),
            start: { clientX: event.clientX, clientY: event.clientY, frame: frameOf(block, index) },
            latest: null,
            latestMap: null
        };
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp, { once: true });
    }, [isEditMode, frameOf, groupMemberIds, onPointerMove, onPointerUp]);

    // Combine every selected object (each expanded to its whole group) into a
    // new group, or split the selected groups apart again with ⌘⇧G.
    const groupSelected = useCallback(() => {
        const ids = new Set(selectedIdsRef.current);
        blocksRef.current.forEach((b) => {
            if (b.groupId && ids.has(b.id)) {
                blocksRef.current.forEach((sib) => { if (sib.groupId === b.groupId) ids.add(sib.id); });
            }
        });
        if (ids.size < 2) return;
        const groupId = uid('grp');
        onUpdateBlocks(blocksRef.current.map((b) => (ids.has(b.id) ? { ...b, groupId } : b)));
        setSelectedIds(Array.from(ids));
    }, [onUpdateBlocks]);

    const ungroupSelected = useCallback(() => {
        const ids = new Set(selectedIdsRef.current);
        let changed = false;
        const next = blocksRef.current.map((b) => {
            if (ids.has(b.id) && b.groupId) {
                changed = true;
                const { groupId, ...rest } = b;
                return rest;
            }
            return b;
        });
        if (changed) onUpdateBlocks(next);
    }, [onUpdateBlocks]);

    // --- Copy / cut / paste -----------------------------------------------
    // The clipboard lives outside the component (see utils/blockClipboard), so
    // an object cut on one slide is still there after navigating to another.
    const copySelection = useCallback(({ cut = false } = {}) => {
        const ids = new Set(selectedIdsRef.current);
        if (!ids.size) return 0;
        // Grab whole groups, the way dragging and deleting already do.
        blocksRef.current.forEach((block) => {
            if (block.groupId && ids.has(block.id)) {
                blocksRef.current.forEach((sib) => { if (sib.groupId === block.groupId) ids.add(sib.id); });
            }
        });
        const copied = blocksRef.current.filter((block) => ids.has(block.id));
        const count = writeBlockClipboard(copied);
        if (cut && count) {
            onUpdateBlocks(blocksRef.current.filter((block) => !ids.has(block.id)));
            setSelectedIds([]);
            // Focus follows the removed object's tab handle, so give it back.
            stageRef.current?.focus({ preventScroll: true });
        }
        return count;
    }, [onUpdateBlocks]);

    // Repeat pastes cascade rather than restacking on the same spot.
    const pasteRunRef = useRef({ signature: null, count: 0 });

    const pasteClipboard = useCallback(() => {
        const clipboard = readBlockClipboard();
        if (!clipboard.length) return 0;

        // Pasting the same clipboard again on the same slide steps further out;
        // a new copy, or a different slide, starts the cascade over.
        const signature = clipboard.map((block) => block.id).join('|');
        const currentIds = new Set(blocksRef.current.map((block) => block.id));
        const alreadyHere = clipboard.some((block) => currentIds.has(block.id));
        const run = pasteRunRef.current;
        run.count = run.signature === signature ? run.count + 1 : 1;
        run.signature = signature;
        // Pasting onto a slide the objects are not on can land at their own
        // coordinates; only a same-slide paste has to dodge the original.
        const offset = alreadyHere || run.count > 1 ? 3 * run.count : 0;

        const pasted = prepareBlocksForPaste(clipboard, () => uid('block'), { offset, normalizeFrame });
        onUpdateBlocks([...blocksRef.current, ...pasted]);
        setSelectedIds(pasted.map((block) => block.id));
        return pasted.length;
    }, [onUpdateBlocks]);

    const handleStageKeyDown = useCallback((event) => {
        if (!isEditMode) return;
        // Don't act on keyboard shortcuts while the user is editing card content.
        if (isEditableTarget(event.target)) return;

        // ⌘/Ctrl+G groups the selection; add Shift to ungroup.
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'g') {
            event.preventDefault();
            if (event.shiftKey) ungroupSelected();
            else groupSelected();
            return;
        }

        // ⌘/Ctrl+A selects every object on the slide.
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
            event.preventDefault();
            setSelectedIds(blocks.map((b) => b.id));
            return;
        }

        if (event.key === 'Escape') { setSelectedIds([]); return; }

        if (!selectedIds.length) return;

        // Enter/F2 on a single selected text object starts typing in place,
        // matching the double-click behaviour (PowerPoint's F2).
        if (event.key === 'Enter' || event.key === 'F2') {
            const target = blocks.find((b) => b.id === selectedIds[0]);
            if (selectedIds.length === 1 && target && beginInlineEdit(target)) {
                event.preventDefault();
                return;
            }
        }

        // Delete every selected object. A single selection routes through the
        // parent's confirm dialog (toolbar parity); multiple delete at once.
        if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();
            const ids = selectedIds;
            setSelectedIds([]);
            if (ids.length === 1) {
                onDeleteBlock?.(ids[0]);
            } else {
                onUpdateBlocks(blocks.filter((b) => !ids.includes(b.id)));
            }
            // The focused tab handle disappears with its object, so hand focus
            // back to the stage and keep the keyboard working.
            stageRef.current?.focus({ preventScroll: true });
            return;
        }

        const deltas = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const delta = deltas[event.key];
        if (!delta) return;
        event.preventDefault();
        const step = event.shiftKey ? 4 : 1;
        const frameMap = {};
        blocks.forEach((b, index) => {
            if (!selectedIds.includes(b.id)) return;
            const current = frameOf(b, index);
            frameMap[b.id] = normalizeFrame({
                ...current,
                x: current.x + delta[0] * step,
                y: current.y + delta[1] * step
            });
        });
        commitFrames(frameMap);
    }, [isEditMode, selectedIds, blocks, frameOf, commitFrames, onDeleteBlock, onUpdateBlocks,
        groupSelected, ungroupSelected, beginInlineEdit]);

    // Copy/cut/paste listen on the document rather than the canvas.
    //
    // The canvas-scoped shortcuts (delete, nudge, group) only make sense with
    // the canvas focused, but paste does not: the natural move is to cut, click
    // the arrow to the next slide — focus now on that button — and press
    // ⌘/Ctrl+V. Requiring a click on the canvas first would feel broken. The
    // handler still stands down whenever the caret is in a field or an editor,
    // or while a content modal is open, so it never eats a real text paste.
    useEffect(() => {
        if (!isEditMode) return undefined;
        const onKeyDown = (event) => {
            if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
            const key = event.key.toLowerCase();
            if (key !== 'c' && key !== 'x' && key !== 'v') return;
            if (isEditableTarget(event.target)) return;
            if (document.querySelector('[data-app-modal]')) return;
            // A text selection means the user is copying words, not objects.
            if (key !== 'v' && !window.getSelection()?.isCollapsed) return;

            if (key === 'v') {
                if (pasteClipboard()) event.preventDefault();
                return;
            }
            if (!selectedIdsRef.current.length) return;
            event.preventDefault();
            copySelection({ cut: key === 'x' });
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isEditMode, copySelection, pasteClipboard]);

    // Step a text block's font size up or down. This drives the block's
    // `frame.s` text-scale multiplier — the same value the corner handles set —
    // so it scales the block's text on the canvas and carries into the export.
    const adjustTextScale = useCallback((block, index, delta) => {
        const current = frameOf(block, index);
        const nextScale = Math.min(
            MAX_TEXT_SCALE,
            Math.max(MIN_TEXT_SCALE, Math.round(((current.s || 1) + delta) * 100) / 100)
        );
        commitFrame(block.id, normalizeFrame({ ...current, s: nextScale }));
    }, [frameOf, commitFrame]);

    const resetTextScale = useCallback((block, index) => {
        const current = frameOf(block, index);
        commitFrame(block.id, normalizeFrame({ ...current, s: 1 }));
    }, [frameOf, commitFrame]);

    // Size a media block's frame height to hug its content at the current
    // frame width, so the selection gizmo matches the visible media
    const fitFrameToContent = useCallback((block, index) => {
        const naturalHeight = measurementsRef.current[block.id];
        const stage = stageRef.current;
        if (!naturalHeight || !stage?.clientHeight) return;
        const current = normalizeFrame(block.frame, index);
        const fittedH = Math.min(96, Math.max(4, (naturalHeight / stage.clientHeight) * 100 + 1));
        if (Math.abs(fittedH - current.h) < 1.5) return;
        commitFrame(block.id, normalizeFrame({ ...current, h: fittedH }));
    }, [commitFrame]);

    const handleMeasure = useCallback((block, index, naturalHeight) => {
        measurementsRef.current[block.id] = naturalHeight;
        // Auto-fit only blocks that have never been positioned (fresh imports)
        if (!block.frame && !autoFittedRef.current.has(block.id)) {
            autoFittedRef.current.add(block.id);
            fitFrameToContent(block, index);
        }
    }, [fitFrameToContent]);

    const changeDepth = useCallback((blockId, direction) => {
        const index = blocks.findIndex((b) => b.id === blockId);
        const target = index + direction;
        if (index === -1 || target < 0 || target >= blocks.length) return;
        const next = [...blocks];
        [next[index], next[target]] = [next[target], next[index]];
        onUpdateBlocks(next);
    }, [blocks, onUpdateBlocks]);

    // Split a media block's citation out into its own movable object: a
    // standalone apa-citation block positioned in a full-width row beneath the
    // image or video. Because it is a real apa-citation block it is picked up by
    // the reference-list collector and can be saved to the citation library, and
    // it renders as a bare figure note (no card background). The source block
    // stops rendering/collecting its own note so nothing is duplicated.
    const detachCitation = useCallback((blockId) => {
        const index = blocks.findIndex((b) => b.id === blockId);
        if (index === -1) return;
        const source = blocks[index];
        if (!blockHasCitationContent(source)) return;
        const sourceFrame = frameOf(source, index);
        const citationFrame = normalizeFrame({
            x: sourceFrame.x,
            y: sourceFrame.y + sourceFrame.h + 1,
            w: Math.max(sourceFrame.w, 40),
            h: 7
        });
        const citationBlock = {
            id: uid('block'),
            type: 'apa-citation',
            variant: 'figure-note',
            sourceBlockId: source.id,
            citations: [blockToDetachedCitation(source)],
            frame: citationFrame
        };
        const includeField = INCLUDE_CITATION_FIELD[source.type] || 'imageIncludeCitation';
        const next = blocks.map((b) => (b.id === blockId ? { ...b, [includeField]: false } : b));
        next.splice(index + 1, 0, citationBlock);
        onUpdateBlocks(next);
        setSelectedIds([citationBlock.id]);
    }, [blocks, frameOf, onUpdateBlocks]);

    // Lift the quotes out of a text block into their own APA Quote blocks —
    // the same idea as detaching an image's citation, applied to prose. Each
    // quote becomes a real apa-quote block (so the reference-list collector
    // picks it up), stacked under the text block, and the paragraph it came
    // from is removed so the quote is not shown twice.
    const extractQuotes = useCallback((blockId) => {
        const index = blocks.findIndex((b) => b.id === blockId);
        if (index === -1) return;
        const source = blocks[index];
        const quotes = detectQuotes(source.content);
        if (!quotes.length) return;

        const sourceFrame = frameOf(source, index);
        const width = Math.max(sourceFrame.w, 40);
        // Stack the quotes under the text block while the slide has room. An
        // APA Quote card is tall, so a second one often will not fit — rather
        // than pile it exactly on top of the first, cascade it a little the way
        // a design tool offsets a pasted object, leaving it obvious and easy to
        // drag somewhere sensible.
        let cursorY = sourceFrame.y + sourceFrame.h + 1.5;
        let cascade = 0;
        const quoteBlocks = quotes.map((quote) => {
            const height = estimateQuoteFrameHeight(quote, width);
            let x = sourceFrame.x;
            let y = cursorY;
            if (y + height > 97) {
                cascade += 1;
                x = Math.min(96 - width, sourceFrame.x + cascade * 4);
                y = Math.min(97 - height, 3 + cascade * 4);
            } else {
                cursorY = y + height + 1.5;
            }
            return {
                id: uid('block'),
                ...quoteToBlockData(quote),
                sourceBlockId: source.id,
                frame: normalizeFrame({ x: Math.max(2, x), y: Math.max(2, y), w: width, h: height })
            };
        });

        const remaining = removeQuotesAt(source.content, quotes.map((quote) => quote.elementIndex));
        const next = blocks.map((b) => (b.id === blockId ? { ...b, content: remaining } : b));
        next.splice(index + 1, 0, ...quoteBlocks);
        onUpdateBlocks(next);
        setSelectedIds(quoteBlocks.map((block) => block.id));
    }, [blocks, frameOf, onUpdateBlocks]);

    // --- Marquee (rubber-band) selection ----------------------------------
    // Dragging across the empty canvas sweeps a selection box; every object it
    // touches (expanded to whole groups) becomes selected on release.
    const onMarqueePointerMove = useCallback((event) => {
        const drag = marqueeRef.current;
        if (!drag) return;
        const x = ((event.clientX - drag.rect.left) / drag.rect.width) * 100;
        const y = ((event.clientY - drag.rect.top) / drag.rect.height) * 100;
        drag.cur = { x: Math.min(100, Math.max(0, x)), y: Math.min(100, Math.max(0, y)) };
        setMarquee({ ...drag });
    }, []);

    const onMarqueePointerUp = useCallback(() => {
        const drag = marqueeRef.current;
        marqueeRef.current = null;
        window.removeEventListener('pointermove', onMarqueePointerMove);
        setMarquee(null);
        if (!drag) return;
        const box = {
            x: Math.min(drag.origin.x, drag.cur.x),
            y: Math.min(drag.origin.y, drag.cur.y),
            w: Math.abs(drag.cur.x - drag.origin.x),
            h: Math.abs(drag.cur.y - drag.origin.y)
        };
        // A tiny box is really just a click on empty canvas → clear selection.
        if (box.w < 1 && box.h < 1) { setSelectedIds([]); return; }
        const hitGroups = new Set();
        const hits = [];
        blocksRef.current.forEach((b, i) => {
            if (framesIntersect(frameOf(b, i), box)) {
                if (b.groupId) hitGroups.add(b.groupId);
                else hits.push(b.id);
            }
        });
        blocksRef.current.forEach((b) => {
            if (b.groupId && hitGroups.has(b.groupId)) hits.push(b.id);
        });
        setSelectedIds((prev) => (drag.additive ? Array.from(new Set([...prev, ...hits])) : hits));
    }, [onMarqueePointerMove, frameOf]);

    const onStagePointerDown = useCallback((event) => {
        if (!isEditMode || !stageRef.current) return;
        const rect = stageRef.current.getBoundingClientRect();
        const origin = {
            x: ((event.clientX - rect.left) / rect.width) * 100,
            y: ((event.clientY - rect.top) / rect.height) * 100
        };
        marqueeRef.current = { rect, origin, cur: origin, additive: event.shiftKey || event.metaKey || event.ctrlKey };
        setMarquee(marqueeRef.current);
        window.addEventListener('pointermove', onMarqueePointerMove);
        window.addEventListener('pointerup', onMarqueePointerUp, { once: true });
    }, [isEditMode, onMarqueePointerMove, onMarqueePointerUp]);

    // --- Drag an image file straight onto the canvas ----------------------
    // Hands the dropped image(s) to the app, which opens the image importer
    // modal pre-loaded with the file, ready for setup.
    const hasImageFiles = (event) => Array.from(event.dataTransfer?.items || [])
        .some((item) => item.kind === 'file' && item.type.startsWith('image/'));

    const onStageDragOver = useCallback((event) => {
        if (!isEditMode || !onCanvasImageDrop) return;
        if (!hasImageFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsFileOver(true);
    }, [isEditMode, onCanvasImageDrop]);

    const onStageDragLeave = useCallback((event) => {
        // Only clear when the pointer actually leaves the stage, not when it
        // crosses onto a child element.
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setIsFileOver(false);
    }, []);

    const onStageDrop = useCallback((event) => {
        if (!isEditMode || !onCanvasImageDrop) return;
        setIsFileOver(false);
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith('image/'));
        if (!files.length) return;
        event.preventDefault();
        onCanvasImageDrop(files);
    }, [isEditMode, onCanvasImageDrop]);

    // --- Ruler guides -----------------------------------------------------
    // A guide's axis is its orientation: 'v' = vertical line (positioned by an
    // x%), 'h' = horizontal line (positioned by a y%). The top ruler drops
    // horizontal guides; the left ruler drops vertical ones — like Illustrator.
    const clearGuides = useCallback(() => setGuides(emptyGuides()), []);

    const guidePosFromEvent = useCallback((axis, rect, event) => {
        if (axis === 'v') return ((event.clientX - rect.left) / rect.width) * 100;
        return ((event.clientY - rect.top) / rect.height) * 100;
    }, []);

    const onGuidePointerMove = useCallback((event) => {
        const drag = guideDragRef.current;
        if (!drag) return;
        const pos = guidePosFromEvent(drag.axis, drag.rect, event);
        // Allow the pointer to travel slightly off-stage so an existing guide
        // can be dragged away to delete it.
        const clamped = Math.min(110, Math.max(-10, pos));
        // Keep the ref in sync so the pointer-up commit reads the final
        // position (not the stale one captured when the drag began).
        drag.pos = clamped;
        setGuideDrag({ ...drag });
    }, [guidePosFromEvent]);

    const onGuidePointerUp = useCallback(() => {
        const drag = guideDragRef.current;
        guideDragRef.current = null;
        window.removeEventListener('pointermove', onGuidePointerMove);
        if (!drag) { setGuideDrag(null); return; }

        const { axis, index, origin, pos } = drag;
        const inStage = pos >= 0 && pos <= 100;
        setGuides((prev) => {
            const list = [...(prev[axis] || [])];
            if (origin === 'existing') {
                if (inStage) list[index] = tidyGuidePosition(pos);
                else list.splice(index, 1); // dragged off-stage → delete
            } else if (inStage) {
                list.push(tidyGuidePosition(pos)); // new guide from the ruler
            }
            return sanitizeGuides({ ...prev, [axis]: list });
        });
        setGuideDrag(null);
    }, [onGuidePointerMove]);

    const beginGuide = useCallback((event, axis, index = null) => {
        if (!isEditMode || !stageRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = stageRef.current.getBoundingClientRect();
        const origin = index === null ? 'ruler' : 'existing';
        const pos = origin === 'existing'
            ? guides[axis][index]
            : guidePosFromEvent(axis, rect, event);
        guideDragRef.current = { axis, rect, index, origin, pos };
        setGuideDrag({ axis, pos, index, origin });
        window.addEventListener('pointermove', onGuidePointerMove);
        window.addEventListener('pointerup', onGuidePointerUp, { once: true });
    }, [isEditMode, guides, guidePosFromEvent, onGuidePointerMove, onGuidePointerUp]);

    const removeGuide = useCallback((axis, index) => {
        setGuides((prev) => {
            const list = [...(prev[axis] || [])];
            list.splice(index, 1);
            return { ...prev, [axis]: list };
        });
    }, []);

    const rulersActive = isEditMode && showRulers;
    const guideCount = guides.v.length + guides.h.length;
    // The guide being repositioned is hidden while its live preview is shown.
    const draggingExisting = guideDrag && guideDrag.origin === 'existing' ? guideDrag : null;

    // Bounding box (percent coords) for every group, so grouped objects show a
    // dashed frame around the whole set — the visual cue that they're grouped.
    const groupBoxes = useMemo(() => {
        const acc = {};
        blocks.forEach((b, i) => {
            if (!b.groupId) return;
            const f = frameOf(b, i);
            const g = acc[b.groupId] || { x0: 100, y0: 100, x1: 0, y1: 0 };
            g.x0 = Math.min(g.x0, f.x);
            g.y0 = Math.min(g.y0, f.y);
            g.x1 = Math.max(g.x1, f.x + f.w);
            g.y1 = Math.max(g.y1, f.y + f.h);
            acc[b.groupId] = g;
        });
        return acc;
    }, [blocks, frameOf]);

    const selectedGroupIds = new Set(
        blocks.filter((b) => b.groupId && selectedIds.includes(b.id)).map((b) => b.groupId)
    );
    const canGroup = selectedIds.length >= 2;
    const canUngroup = selectedGroupIds.size > 0;

    return (
        <div className={`slide-designer-frame ${rulersActive ? 'slide-designer-frame--rulers' : ''}`}>
            {isEditMode && (
                <div className="slide-designer__rulerbar" onPointerDown={(e) => e.stopPropagation()}>
                    {(canGroup || canUngroup) && (
                        <>
                            {canGroup && (
                                <button
                                    type="button"
                                    className="slide-designer__rulerbtn"
                                    onClick={groupSelected}
                                    title="Group the selected objects so they move and resize together (Ctrl/⌘+G)."
                                >
                                    ⧉ Group ({selectedIds.length})
                                </button>
                            )}
                            {canUngroup && (
                                <button
                                    type="button"
                                    className="slide-designer__rulerbtn"
                                    onClick={ungroupSelected}
                                    title="Break the selected group apart (Ctrl/⌘+Shift+G)."
                                >
                                    ⤬ Ungroup
                                </button>
                            )}
                        </>
                    )}
                    {/* The rulers/guides on-off toggle now lives in the mode
                        banner (kept off the title bar). This bar keeps the
                        contextual guide + grouping controls. */}
                    {rulersActive && (
                        <button
                            type="button"
                            className="slide-designer__rulerbtn"
                            onClick={clearGuides}
                            disabled={guideCount === 0}
                            title="Remove all guide lines. Guides otherwise stay exactly where you place them across slides and reloads."
                        >
                            Reset guides{guideCount > 0 ? ` (${guideCount})` : ''}
                        </button>
                    )}
                </div>
            )}
            {rulersActive && (
                <>
                    <div className="slide-ruler-corner" aria-hidden="true" />
                    <div
                        className="slide-ruler slide-ruler--top"
                        onPointerDown={(e) => beginGuide(e, 'h')}
                        title="Drag down to add a horizontal guide"
                    >
                        {MINOR_TICKS.map((p) => (
                            <span key={`tm${p}`} className="slide-ruler__tick slide-ruler__tick--minor" style={{ left: `${p}%` }} />
                        ))}
                        {MAJOR_TICKS.map((p) => (
                            <span key={`tM${p}`} className="slide-ruler__tick slide-ruler__tick--major" style={{ left: `${p}%` }}>
                                <span className="slide-ruler__label">{p}</span>
                            </span>
                        ))}
                    </div>
                    <div
                        className="slide-ruler slide-ruler--left"
                        onPointerDown={(e) => beginGuide(e, 'v')}
                        title="Drag right to add a vertical guide"
                    >
                        {MINOR_TICKS.map((p) => (
                            <span key={`lm${p}`} className="slide-ruler__tick slide-ruler__tick--minor" style={{ top: `${p}%` }} />
                        ))}
                        {MAJOR_TICKS.map((p) => (
                            <span key={`lM${p}`} className="slide-ruler__tick slide-ruler__tick--major" style={{ top: `${p}%` }}>
                                <span className="slide-ruler__label">{p}</span>
                            </span>
                        ))}
                    </div>
                </>
            )}
        <div
            ref={stageRef}
            className={`slide-designer ${isEditMode ? 'slide-designer--edit' : ''} ${isFileOver ? 'slide-designer--fileover' : ''}`}
            tabIndex={isEditMode ? 0 : -1}
            onKeyDown={handleStageKeyDown}
            onPointerDown={onStagePointerDown}
            onDragOver={onStageDragOver}
            onDragLeave={onStageDragLeave}
            onDrop={onStageDrop}
        >
            {/* Dashed frame around each group so grouped objects read as a set. */}
            {isEditMode && Object.entries(groupBoxes).map(([groupId, box]) => (
                <div
                    key={`group-${groupId}`}
                    className={`slide-designer__groupbox ${selectedGroupIds.has(groupId) ? 'is-selected' : ''}`}
                    style={{
                        left: `${box.x0}%`,
                        top: `${box.y0}%`,
                        width: `${box.x1 - box.x0}%`,
                        height: `${box.y1 - box.y0}%`
                    }}
                    aria-hidden="true"
                >
                    <span className="slide-designer__grouptag">⧉ Group</span>
                </div>
            ))}
            {blocks.map((block, index) => {
                const frame = frameOf(block, index);
                const isSelected = isEditMode && selectedIds.includes(block.id);
                const isSoleSelection = isEditMode && selectedId === block.id;
                const canDetach = blockHasCitationContent(block);
                // Only offered when the block actually holds a cited quote, so
                // the toolbar stays quiet on ordinary prose. Counted only for
                // the selected block — parsing every block's HTML on every
                // render would be wasted work on a busy slide.
                const quoteCount = isSoleSelection && INLINE_TEXT_TYPES.has(block.type)
                    ? countQuotes(block.content)
                    : 0;
                const canEditInline = isEditMode && INLINE_TEXT_TYPES.has(block.type);
                const isInlineEditing = inlineEdit?.blockId === block.id;
                return (
                    <div
                        key={block.id}
                        data-block-id={block.id}
                        className={`slide-designer__item ${isSelected ? 'slide-designer__item--selected' : ''} ${block.groupId ? 'slide-designer__item--grouped' : ''} ${isInlineEditing ? 'slide-designer__item--editing' : ''}`}
                        style={{
                            left: `${frame.x}%`,
                            top: `${frame.y}%`,
                            width: `${frame.w}%`,
                            height: `${frame.h}%`,
                            // Blocks stack in document order; the one being typed
                            // into jumps to the front so neither its editing
                            // surface nor its ribbon can be covered by a
                            // later block.
                            zIndex: isInlineEditing ? 500 : 10 + index,
                            '--text-scale': frame.s || 1
                        }}
                        onPointerDown={(e) => {
                            if (isEditMode) {
                                e.stopPropagation();
                                selectBlock(block, e.shiftKey || e.metaKey || e.ctrlKey);
                            }
                        }}
                    >
                        <div className="slide-designer__content">
                            {isInlineEditing ? null : SCALED_MEDIA_TYPES.has(block.type)
                                ? (
                                    <ScaleToFit onMeasure={(naturalHeight) => handleMeasure(block, index, naturalHeight)}>
                                        {renderBlock(block)}
                                    </ScaleToFit>
                                )
                                : renderBlock(block)}
                        </div>
                        {isInlineEditing && (
                            <InlineTextEditor
                                block={block}
                                ribbonBelow={frame.y < 9}
                                ribbonRight={frame.x + frame.w > 62}
                                caretPoint={inlineEdit.caretPoint}
                                onCommit={(html, options) => commitInlineEdit(block.id, html, options)}
                                onOpenModal={(html) => escalateToModal(block.id, html)}
                            />
                        )}
                        {isEditMode && !isInlineEditing && (
                            <div
                                className="slide-designer__capture"
                                onPointerDown={(e) => startDrag(e, block, index, 'move')}
                                onDoubleClick={(e) => {
                                    // Text objects are edited straight on the slide;
                                    // everything else opens the full editor modal.
                                    if (beginInlineEdit(block, { x: e.clientX, y: e.clientY })) return;
                                    onEditBlock?.(block.id);
                                }}
                                title={canEditInline
                                    ? 'Drag to move · double-click to edit text here · Shift-click to multi-select'
                                    : 'Drag to move · double-click to edit · Shift-click to multi-select'}
                            />
                        )}
                        {isSelected && !isInlineEditing && (
                            <div
                                className={`slide-designer__tab ${frame.y < 4 ? 'slide-designer__tab--inside' : ''}`}
                                role="button"
                                tabIndex={0}
                                data-keep-focus="1"
                                aria-label={`${tabLabel(block)} object selected. Press Delete or Backspace to remove it.`}
                                title={`${tabLabel(block)} · press Delete or Backspace to remove · drag to move · Esc to deselect`}
                                onPointerDown={(e) => {
                                    // Focus the tab first: startDrag suppresses the
                                    // browser's own focus so the drag stays smooth.
                                    e.currentTarget.focus({ preventScroll: true });
                                    startDrag(e, block, index, 'move');
                                }}
                            >
                                <span className="slide-designer__tab-grip" aria-hidden="true">⠿</span>
                                {tabLabel(block)}
                            </div>
                        )}
                        {isSoleSelection && !isInlineEditing && (
                            <>
                                {RESIZE_HANDLES.map((handle) => {
                                    const isCorner = handle.length === 2;
                                    const scalesText = isCorner && !SCALED_MEDIA_TYPES.has(block.type);
                                    return (
                                        <div
                                            key={handle}
                                            className={`slide-designer__handle slide-designer__handle--${handle}`}
                                            title={scalesText ? 'Drag corner to scale text with the box' : 'Drag to resize'}
                                            onPointerDown={(e) => startDrag(e, block, index, 'resize', handle)}
                                        />
                                    );
                                })}
                                {/* The toolbar normally floats above the object, but
                                    the stage clips at its own top edge and the slide
                                    header sits right above it — so an object near the
                                    top would have its controls cut off. Then it flips
                                    inside, the way the selection tab and the inline
                                    ribbon already do. */}
                                <div
                                    className={`slide-designer__toolbar ${frame.y < TOOLBAR_FLIP_Y ? 'slide-designer__toolbar--inside' : ''}`}
                                    onPointerDown={(e) => e.stopPropagation()}
                                >
                                    <button type="button" onClick={() => onEditBlock?.(block.id)} title="Edit content in the full editor modal">✏️</button>
                                    {canEditInline && (
                                        <button
                                            type="button"
                                            className="slide-designer__tooltext"
                                            onClick={() => beginInlineEdit(block)}
                                            title="Edit the text here on the slide (or double-click the block)"
                                        >
                                            T
                                        </button>
                                    )}
                                    {SCALED_MEDIA_TYPES.has(block.type) && (
                                        <button type="button" onClick={() => fitFrameToContent(block, index)} title="Fit frame to content">⛶</button>
                                    )}
                                    {!SCALED_MEDIA_TYPES.has(block.type) && (
                                        <span className="slide-designer__toolgroup">
                                            <button type="button" onClick={() => adjustTextScale(block, index, -0.1)} title="Decrease text size">A−</button>
                                            <button
                                                type="button"
                                                onClick={() => resetTextScale(block, index)}
                                                title="Reset text size to 100%"
                                                className="slide-designer__toolreadout"
                                            >
                                                {Math.round((frame.s || 1) * 100)}%
                                            </button>
                                            <button type="button" onClick={() => adjustTextScale(block, index, 0.1)} title="Increase text size">A+</button>
                                        </span>
                                    )}
                                    {canDetach && (
                                        <button type="button" onClick={() => detachCitation(block.id)} title="Detach the citation into its own movable object">🔖</button>
                                    )}
                                    {quoteCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => extractQuotes(block.id)}
                                            title={quoteCount === 1
                                                ? 'Pop the quote out into its own APA Quote block'
                                                : `Pop ${quoteCount} quotes out into their own APA Quote blocks`}
                                        >
                                            ❝{quoteCount > 1 ? quoteCount : ''}
                                        </button>
                                    )}
                                    <button type="button" onClick={() => changeDepth(block.id, 1)} title="Bring forward">⬆</button>
                                    <button type="button" onClick={() => changeDepth(block.id, -1)} title="Send backward">⬇</button>
                                    <button type="button" onClick={() => onDeleteBlock?.(block.id)} title="Delete block">🗑</button>
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
            {marquee && (() => {
                const box = {
                    x: Math.min(marquee.origin.x, marquee.cur.x),
                    y: Math.min(marquee.origin.y, marquee.cur.y),
                    w: Math.abs(marquee.cur.x - marquee.origin.x),
                    h: Math.abs(marquee.cur.y - marquee.origin.y)
                };
                if (box.w < 0.4 && box.h < 0.4) return null;
                return (
                    <div
                        className="slide-designer__marquee"
                        style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }}
                        aria-hidden="true"
                    />
                );
            })()}
            {isFileOver && (
                <div className="slide-designer__dropzone" aria-hidden="true">
                    <span>Drop image to add it to this slide</span>
                </div>
            )}
            {rulersActive && (
                <div className="slide-guides" aria-hidden="true">
                    {guides.v.map((pos, i) => (
                        (draggingExisting && draggingExisting.axis === 'v' && draggingExisting.index === i)
                            ? null
                            : (
                                <div
                                    key={`gv${i}`}
                                    className="slide-guide slide-guide--v"
                                    style={{ left: `${pos}%` }}
                                    onPointerDown={(e) => beginGuide(e, 'v', i)}
                                    onDoubleClick={(e) => { e.stopPropagation(); removeGuide('v', i); }}
                                    title="Drag to move · double-click (or drag off canvas) to delete"
                                />
                            )
                    ))}
                    {guides.h.map((pos, i) => (
                        (draggingExisting && draggingExisting.axis === 'h' && draggingExisting.index === i)
                            ? null
                            : (
                                <div
                                    key={`gh${i}`}
                                    className="slide-guide slide-guide--h"
                                    style={{ top: `${pos}%` }}
                                    onPointerDown={(e) => beginGuide(e, 'h', i)}
                                    onDoubleClick={(e) => { e.stopPropagation(); removeGuide('h', i); }}
                                    title="Drag to move · double-click (or drag off canvas) to delete"
                                />
                            )
                    ))}
                    {guideDrag && guideDrag.pos >= 0 && guideDrag.pos <= 100 && (
                        <div
                            className={`slide-guide slide-guide--${guideDrag.axis} slide-guide--live`}
                            style={guideDrag.axis === 'v'
                                ? { left: `${guideDrag.pos}%` }
                                : { top: `${guideDrag.pos}%` }}
                        >
                            <span className="slide-guide__readout">{Math.round(guideDrag.pos)}%</span>
                        </div>
                    )}
                </div>
            )}
        </div>
        </div>
    );
};

export default SlideDesigner;
